// The date-range calendar inside the custom-range popover. All month math
// lives in `lib/calendar.ts` (pure + tested); this file owns only the grid's
// interaction state: which end of the range is being picked, what the hover
// preview looks like, and which cell holds keyboard focus.
import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from "react";

import {
  type CalDate,
  WEEKDAY_LABELS,
  addDays,
  addMonths,
  calDateLabel,
  compareCalDates,
  fromCalDate,
  isSameCalDate,
  isWithinRange,
  monthLabel,
  monthMatrix,
  toCalDate,
  todayUtc,
} from "../lib/calendar";
import { formatBucketLabel } from "../lib/bucket-format";

export interface RangeCalendarProps {
  /** The draft range's current bounds, as `YYYY-MM-DD` (a moment bound's day
   *  part is fine — `toCalDate` ignores its clock). */
  start: string | null;
  end: string | null;
  /** Both bounds together, always — this control never emits half a range,
   *  matching the backend's own all-or-nothing contract. */
  onSelect: (start: CalDate, end: CalDate) => void;
}

export function RangeCalendar({ start, end, onSelect }: RangeCalendarProps) {
  const today = todayUtc();
  const startDate = toCalDate(start);
  const endDate = toCalDate(end);

  // The first click of a new range. While it is set the range is HALF picked,
  // and `hover` supplies the provisional other end so the reader can see the
  // span before committing to it.
  const [anchor, setAnchor] = useState<CalDate | null>(null);
  const [hover, setHover] = useState<CalDate | null>(null);

  const [view, setView] = useState(() => {
    const seed = startDate ?? today;
    return { year: seed.year, month: seed.month };
  });

  // Roving tabindex: exactly ONE day is in the tab order at a time. A grid of
  // 42 individually tabbable buttons would bury the Apply button ~40 stops
  // deep; arrow keys move within the grid instead, which is the standard
  // date-grid pattern and what a keyboard reader will already expect.
  const [focusDay, setFocusDay] = useState<CalDate>(() => startDate ?? today);
  // Only steal focus when the reader actually drove the grid from the
  // keyboard, never on an ordinary re-render (which would yank focus out of
  // the time inputs beside it every time a draft value changed).
  const shouldRefocus = useRef(false);
  const gridRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    if (!shouldRefocus.current) return;
    shouldRefocus.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>('button[tabindex="0"]')?.focus();
  }, [focusDay]);

  const moveFocus = (next: CalDate) => {
    shouldRefocus.current = true;
    setFocusDay(next);
    // Follow the cursor out of the visible month rather than silently moving
    // focus to a cell that isn't rendered.
    if (next.year !== view.year || next.month !== view.month) {
      setView({ year: next.year, month: next.month });
    }
  };

  const pick = (day: CalDate) => {
    if (anchor === null) {
      setAnchor(day);
      setHover(day);
      return;
    }
    // Second click completes it, in whichever order the two were clicked.
    const [lo, hi] = compareCalDates(anchor, day) <= 0 ? [anchor, day] : [day, anchor];
    setAnchor(null);
    setHover(null);
    onSelect(lo, hi);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLTableSectionElement>) => {
    const deltas: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    const delta = deltas[event.key];
    if (delta !== undefined) {
      event.preventDefault();
      moveFocus(addDays(focusDay, delta));
      return;
    }
    if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      const next = addMonths(focusDay.year, focusDay.month, event.key === "PageUp" ? -1 : 1);
      // Clamp into the target month: paging from the 31st into a 30-day month
      // must land on a real day, not roll into the month after.
      const lastDay = monthMatrix(next.year, next.month).flat().filter(Boolean).length;
      moveFocus({ year: next.year, month: next.month, day: Math.min(focusDay.day, lastDay) });
    }
  };

  // The range to PAINT: the committed one, or the provisional one being
  // dragged out from the anchor.
  const paintFrom = anchor ?? startDate;
  const paintTo = anchor ? hover : endDate;

  const goMonth = (delta: number) => setView(addMonths(view.year, view.month, delta));

  return (
    <div className="bi-cal">
      <div className="bi-cal__head">
        <button
          type="button"
          className="bi-cal__nav"
          onClick={() => goMonth(-1)}
          aria-label="Previous month"
        >
          {"‹"}
        </button>
        <span className="bi-cal__month" aria-live="polite">
          {monthLabel(view.year, view.month)}
        </span>
        <button
          type="button"
          className="bi-cal__nav"
          onClick={() => goMonth(1)}
          aria-label="Next month"
        >
          {"›"}
        </button>
      </div>
      <table className="bi-cal__grid" role="grid" aria-label="Pick a date range">
        <thead>
          <tr>
            {WEEKDAY_LABELS.map((label) => (
              <th key={label} scope="col" className="bi-cal__weekday">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody
          ref={gridRef}
          onKeyDown={onKeyDown}
          onMouseLeave={() => {
            if (anchor) setHover(null);
          }}
        >
          {monthMatrix(view.year, view.month).map((week, wi) => (
            <tr key={wi}>
              {week.map((day, di) => {
                if (day === null) return <td key={di} className="bi-cal__cell" />;
                const isStart = isSameCalDate(day, paintFrom);
                const isEnd = isSameCalDate(day, paintTo);
                const inRange = isWithinRange(day, paintFrom, paintTo);
                const classes = ["bi-cal__day"];
                if (inRange) classes.push("bi-cal__day--in-range");
                if (isStart || isEnd) classes.push("bi-cal__day--edge");
                if (isSameCalDate(day, today)) classes.push("bi-cal__day--today");
                return (
                  <td key={di} className="bi-cal__cell">
                    <button
                      type="button"
                      className={classes.join(" ")}
                      tabIndex={isSameCalDate(day, focusDay) ? 0 : -1}
                      aria-label={calDateLabel(day)}
                      aria-pressed={inRange}
                      onClick={() => {
                        setFocusDay(day);
                        pick(day);
                      }}
                      onMouseEnter={() => {
                        if (anchor) setHover(day);
                      }}
                    >
                      {day.day}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="bi-cal__hint">
        {anchor
          ? `From ${formatBucketLabel(fromCalDate(anchor), "P1D")}. Pick the end of the range.`
          : "Pick the start of the range."}
      </p>
    </div>
  );
}
